import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SalesProvider } from "@/contexts/SalesContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Assessments from "@/pages/Assessments";
import AssessmentDetail from "@/pages/AssessmentDetail";
import PublicQuestionnaire from "@/pages/PublicQuestionnaire";
import AEPReport from "@/pages/AEPReport";
import PrintQuestionnaire from "@/pages/PrintQuestionnaire";
import CompanyHeatMap from "@/pages/CompanyHeatMap";
import Users from "@/pages/admin/Users";
import Audit from "@/pages/admin/Audit";
import Leads from "@/pages/admin/Leads";
import AdminSettings from "@/pages/admin/Settings";
import FinanceiroAdmin from "@/pages/admin/Financeiro";
import DownloadsManager from "@/pages/admin/DownloadsManager";
import CompanyHub from "@/pages/companies/CompanyHub";
import CompanyDossier from "@/pages/companies/CompanyDossier";
import {
  CompanyDashboard, CompanyAvaliacoes, CompanyRiscos, CompanyPgr,
  CompanyPlanoAcao, CompanyAbsenteismo, CompanyDocumentos, CompanyDownloads,
  CompanyRelatorios, CompanyHistorico, CompanyConfig,
} from "@/pages/companies/CompanyHubTabs";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import ValidateReport from "@/pages/ValidateReport";
import CnpjConsolidated from "@/pages/CnpjConsolidated";
import CnpjAEPReport from "@/pages/CnpjAEPReport";
import CnpjAEPEditor from "@/pages/CnpjAEPEditor";
import CnpjAEPDocumentEditor from "@/pages/CnpjAEPDocumentEditor";
import Pricing from "@/pages/Pricing";
import PricingSuccess from "@/pages/PricingSuccess";
import Team from "@/pages/Team";
import PgrImport from "@/pages/import/PgrImport";
import Absenteeism from "@/pages/absenteeism/Absenteeism";
import AbsEmployees from "@/pages/absenteeism/Employees";
import AbsCertificates from "@/pages/absenteeism/Certificates";
import AbsIntelligence from "@/pages/absenteeism/Intelligence";
import AbsActionPlans from "@/pages/absenteeism/ActionPlans";
import AbsImport from "@/pages/absenteeism/ImportExcel";
import AbsReport from "@/pages/absenteeism/Report";
import RiskMapConsolidated from "@/pages/RiskMapConsolidated";
import AnnualPlan from "@/pages/AnnualPlan";
import Downloads from "@/pages/Downloads";
import DemoVertical from "@/pages/DemoVertical";
import ToolEmbed from "@/pages/ToolEmbed";
import TestCenter from "@/pages/psy/TestCenter";
import NewCampaign from "@/pages/psy/NewCampaign";
import CampaignDetail from "@/pages/psy/CampaignDetail";
import CompanyPsyAggregate from "@/pages/psy/CompanyPsyAggregate";
import PublicPsyForm from "@/pages/psy/PublicPsyForm";
import AIAnalysisPDF from "@/pages/psy/AIAnalysisPDF";
import CompanyAIAnalysisPDF from "@/pages/psy/CompanyAIAnalysisPDF";
import AssessmentAIPdf from "@/pages/assessment/AssessmentAIPdf";
import DashboardAdvanced from "@/pages/DashboardAdvanced";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">
      <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…
    </div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <SalesProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />

            {/* Public anonymous questionnaire */}
            <Route path="/q/:token" element={<PublicQuestionnaire />} />

            {/* Public psychosocial test form (Central de Testes Psicossociais NR-01) */}
            <Route path="/teste/:token" element={<PublicPsyForm />} />

            {/* Premium PDF preview (auth required — protected via API) */}
            <Route path="/teste-pdf/:cid/:aid" element={<Protected><AIAnalysisPDF /></Protected>} />
            <Route path="/empresa-pdf/:companyId/:aid" element={<Protected><CompanyAIAnalysisPDF /></Protected>} />
            <Route path="/teste-pdf-aval/:aid/:anid" element={<Protected><AssessmentAIPdf /></Protected>} />

            {/* Public AEP validation (QR code target) */}
            <Route path="/validar/:aid" element={<ValidateReport />} />

            {/* Password recovery / invite acceptance */}
            <Route path="/recuperar-senha" element={<ForgotPassword />} />
            <Route path="/definir-senha/:token" element={<SetPassword />} />

            {/* Vertical 9:16 demo loop (for screen recording → Reels/TikTok) */}
            <Route path="/demo-vertical" element={<DemoVertical />} />

            {/* AEP report (print) — needs auth */}
            <Route path="/aep/:id" element={<Protected><AEPReport /></Protected>} />
            <Route path="/aep/cnpj/:cnpj" element={<Protected><CnpjAEPReport /></Protected>} />
            <Route path="/aep/cnpj/:cnpj/editor" element={<Protected><CnpjAEPEditor /></Protected>} />
            <Route path="/aep/cnpj/:cnpj/documento" element={<Protected><CnpjAEPDocumentEditor /></Protected>} />
            <Route path="/imprimir/:id" element={<Protected><PrintQuestionnaire /></Protected>} />

            {/* Authed app */}
            <Route path="/app" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard-avancado" element={<DashboardAdvanced />} />
              <Route path="empresas" element={<Companies />} />
              <Route path="empresas/:id" element={<CompanyHub />}>
                <Route index element={<CompanyDashboard />} />
                <Route path="dashboard"   element={<CompanyDashboard />} />
                <Route path="avaliacoes"  element={<CompanyAvaliacoes />} />
                <Route path="riscos"      element={<CompanyRiscos />} />
                <Route path="pgr"         element={<CompanyPgr />} />
                <Route path="plano-acao"  element={<CompanyPlanoAcao />} />
                <Route path="absenteismo" element={<CompanyAbsenteismo />} />
                <Route path="documentos"  element={<CompanyDocumentos />} />
                <Route path="downloads"   element={<CompanyDownloads />} />
                <Route path="relatorios"  element={<CompanyRelatorios />} />
                <Route path="historico"   element={<CompanyHistorico />} />
                <Route path="config"      element={<CompanyConfig />} />
              </Route>
              <Route path="empresas/:id/prontuario" element={<CompanyDossier />} />
              <Route path="empresas/:id/mapa" element={<CompanyHeatMap />} />
              <Route path="cnpj/:cnpj/consolidado" element={<CnpjConsolidated />} />
              <Route path="avaliacoes" element={<Assessments />} />
              <Route path="avaliacoes/:id" element={<AssessmentDetail />} />
              <Route path="equipe" element={<Team />} />
              <Route path="importar/pgr" element={<PgrImport />} />
              <Route path="importar/pgr/:companyId" element={<PgrImport />} />
              <Route path="absenteismo" element={<Absenteeism />} />
              <Route path="absenteismo/colaboradores" element={<AbsEmployees />} />
              <Route path="absenteismo/atestados" element={<AbsCertificates />} />
              <Route path="absenteismo/inteligencia" element={<AbsIntelligence />} />
              <Route path="absenteismo/planos" element={<AbsActionPlans />} />
              <Route path="absenteismo/importar" element={<AbsImport />} />
              <Route path="absenteismo/relatorio" element={<AbsReport />} />
              <Route path="empresas/:companyId/mapa-risco" element={<RiskMapConsolidated />} />
              <Route path="empresas/:companyId/plano-anual" element={<AnnualPlan />} />
              <Route path="admin/usuarios" element={<Users />} />
              <Route path="admin/auditoria" element={<Audit />} />
              <Route path="admin/leads" element={<Leads />} />
              <Route path="admin/configuracoes" element={<AdminSettings />} />
              <Route path="admin/financeiro" element={<FinanceiroAdmin />} />
              <Route path="admin/downloads" element={<DownloadsManager />} />
              <Route path="planos" element={<Pricing />} />
              <Route path="planos/sucesso" element={<PricingSuccess />} />
              <Route path="downloads" element={<Downloads />} />
              <Route path="ferramentas/:slug" element={<ToolEmbed />} />
              {/* Central de Testes Psicossociais NR-01 */}
              <Route path="testes-psicossociais" element={<TestCenter />} />
              <Route path="testes-psicossociais/nova" element={<NewCampaign />} />
              <Route path="testes-psicossociais/empresa/:companyId" element={<CompanyPsyAggregate />} />
              <Route path="testes-psicossociais/:cid" element={<CampaignDetail />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SalesProvider>
    </AuthProvider>
  );
}
